import React, { useRef, useState, useContext, useCallback } from "react";
import {
  MessageSquare, Settings, FileText, Calendar, DollarSign, BarChart2,
  Target, Bell, CheckSquare, Paperclip, Search, Users, Clock, Clipboard,
  HelpCircle, Award, Briefcase, TrendingUp, ShieldCheck, BookOpen, Send, Star,
} from "lucide-react";

import Sidebar         from "./Sidebar";
import LeftChatList    from "./LeftChatList";
import RightRail       from "./RightRail";
import DirectMessageThread from "./DirectMessageThread";

import { AuthContext } from "../../context/AuthContext";
import { useCallContext } from "../../context/CallContext";
import ChatLoginPanel  from "../../components/auth/ChatLoginPanel";

// Decorative scattered icon background for the empty-DM state.
// 32 entries spread across the canvas; centre area kept lighter so the
// foreground "Select a conversation" text reads cleanly on top.
const EMPTY_PATTERN = [
  // top row
  { Icon: Bell,           top: '5%',  left: '6%',  size: 40, rotate:  15 },
  { Icon: MessageSquare,  top: '4%',  left: '22%', size: 32, rotate: -22 },
  { Icon: Calendar,       top: '6%',  left: '38%', size: 48, rotate:  12 },
  { Icon: FileText,       top: '3%',  left: '56%', size: 40, rotate: -18 },
  { Icon: Settings,       top: '7%',  left: '72%', size: 32, rotate:  25 },
  { Icon: Star,           top: '4%',  left: '88%', size: 48, rotate:  -8 },

  // upper-middle row
  { Icon: BarChart2,      top: '22%', left: '4%',  size: 56, rotate:  18 },
  { Icon: Briefcase,      top: '20%', left: '18%', size: 40, rotate: -25 },
  { Icon: Send,           top: '24%', left: '36%', size: 32, rotate:  30 },
  { Icon: Users,          top: '21%', left: '60%', size: 48, rotate: -15 },
  { Icon: TrendingUp,     top: '23%', left: '78%', size: 40, rotate:  10 },
  { Icon: Paperclip,      top: '20%', left: '92%', size: 32, rotate: -28 },

  // middle row — sides only (centre kept clear for foreground text)
  { Icon: Target,         top: '40%', left: '6%',  size: 48, rotate:  20 },
  { Icon: Award,          top: '42%', left: '22%', size: 40, rotate: -12 },
  { Icon: Clock,          top: '40%', left: '78%', size: 40, rotate:  15 },
  { Icon: HelpCircle,     top: '42%', left: '92%', size: 56, rotate: -20 },

  // lower-middle — sides only
  { Icon: BookOpen,       top: '58%', left: '4%',  size: 56, rotate:  12 },
  { Icon: ShieldCheck,    top: '60%', left: '14%', size: 40, rotate: -25 },
  { Icon: Search,         top: '58%', left: '80%', size: 40, rotate:  18 },
  { Icon: DollarSign,     top: '60%', left: '92%', size: 48, rotate: -10 },

  // lower row
  { Icon: Clipboard,      top: '75%', left: '8%',  size: 48, rotate:  22 },
  { Icon: CheckSquare,    top: '74%', left: '24%', size: 40, rotate: -15 },
  { Icon: Bell,           top: '76%', left: '42%', size: 32, rotate:  28 },
  { Icon: Calendar,       top: '74%', left: '60%', size: 40, rotate: -22 },
  { Icon: BookOpen,       top: '76%', left: '76%', size: 32, rotate:  14 },
  { Icon: TrendingUp,     top: '75%', left: '90%', size: 40, rotate: -18 },

  // bottom row
  { Icon: Settings,       top: '90%', left: '6%',  size: 32, rotate:  30 },
  { Icon: FileText,       top: '92%', left: '22%', size: 48, rotate: -10 },
  { Icon: Briefcase,      top: '90%', left: '40%', size: 40, rotate:  18 },
  { Icon: Star,           top: '92%', left: '58%', size: 32, rotate: -25 },
  { Icon: Search,         top: '90%', left: '74%', size: 40, rotate:  12 },
  { Icon: Award,          top: '92%', left: '88%', size: 48, rotate: -22 },
];

const ChatScreen = () => {
  const { user } = useContext(AuthContext);
  const { startCall } = useCallContext();   // app-wide call provider (mounted in App.js)

  const [activeDM,       setActiveDM]       = useState(null);
  const [highlightMsgId, setHighlightMsgId] = useState(null);

  const onMarkDone = useRef(null);   // shared ref between RightRail and DirectMessageThread
  const filesBus   = useRef(null);   // RightRail sets handlers; the thread's WS pushes file events into them

  // ── Stable callbacks for LeftChatList ─────────────────────────────────
  const handleSelectDM = useCallback((dm) => {
    setActiveDM(dm);
  }, []);

  // Navigate from a tagged message in the right rail to the DM thread
  const handleNavigateToTagged = useCallback((taggedMsg) => {
    const senderId   = taggedMsg.sender_id || taggedMsg.senderId;
    const senderName = taggedMsg.senderName || "Unknown";
    if (!senderId) return;
    setActiveDM({
      id:   senderId,
      name: senderName,
      user: { id: senderId, full_name: senderName },
    });
    setHighlightMsgId(taggedMsg.id);
  }, []);

  if (!user) {
    return (
      <div className="w-screen h-screen bg-black flex items-center justify-center">
        <div className="w-[380px] h-[530px] bg-[#0B0B0B] border border-[#00FF99]/30 rounded-2xl shadow-[0_0_25px_#00FF99]/40 p-4">
          <ChatLoginPanel ssoEnabled={true} />
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{CHAT_SHELL_CSS}</style>

      {/* Same fixed 72px left rail as the rest of the app. We mount the single
          <Sidebar/> component directly (not via MessengerLayout) because its
          <main className="kwsb-content"> adds 24px padding + min-height:100vh,
          which would break the chat's full-height flex panes and add a
          horizontal scrollbar. The 72px rail / 62px mobile-bar offset is
          applied on .kwchat-shell below, mirroring the sidebar's own CSS. */}
      <Sidebar />

      <div className="kwchat-shell bg-black text-white">
        <div className="flex h-full overflow-hidden">

        {/* Left conversation list */}
        <aside className="w-80 border-r border-[#00FF99]/20 bg-[#0B0B0B] overflow-y-auto">
          <LeftChatList
            onSelectDM={handleSelectDM}
            onStartCall={startCall}
          />
        </aside>

        {/* Main — DM thread or empty state */}
        <main className="flex-1 flex flex-col bg-[#0B0B0B] min-w-0">
          {activeDM ? (
            <DirectMessageThread
              key={String(activeDM.id)}
              targetUser={activeDM.user || activeDM}
              onStartCall={startCall}
              highlightMsgId={highlightMsgId}
              onHighlightDone={() => setHighlightMsgId(null)}
              onMarkDone={onMarkDone}
              filesBus={filesBus}
            />
          ) : (
            <div style={{
              position:       "relative",
              flex:           1,
              overflow:       "hidden",
              display:        "flex",
              flexDirection:  "column",
              alignItems:     "center",
              justifyContent: "center",
              padding:        "0 32px",
              textAlign:      "center",
              background:     "radial-gradient(ellipse at center, rgba(0,255,153,0.02) 0%, transparent 70%)",
            }}>
              {/* Decorative scattered icon background — purely visual */}
              <div
                aria-hidden="true"
                style={{
                  position:      "absolute",
                  inset:         0,
                  pointerEvents: "none",
                  userSelect:    "none",
                }}
              >
                {EMPTY_PATTERN.map(({ Icon, top, left, size, rotate }, i) => (
                  <Icon
                    key={i}
                    size={size}
                    strokeWidth={1.5}
                    style={{
                      position:       "absolute",
                      top,
                      left,
                      opacity:        i % 7 === 0 ? 0.22 : i % 4 === 0 ? 0.18 : 0.12,
                      color:          "#00FF99",
                      transform:      `rotate(${rotate}deg)`,
                      pointerEvents:  "none",
                    }}
                  />
                ))}
              </div>

              {/* Foreground content */}
              <div style={{
                position:      "relative",
                zIndex:        1,
                display:       "flex",
                flexDirection: "column",
                alignItems:    "center",
              }}>
                <MessageSquare
                  size={48}
                  strokeWidth={1.25}
                  style={{ color: "rgba(255,255,255,0.15)" }}
                />
                <p style={{ color: "#ffffff", fontSize: 16, fontWeight: 500, marginTop: 16, marginBottom: 0 }}>
                  Select a conversation to start messaging
                </p>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 4, marginBottom: 0 }}>
                  Or use K.I.R.A (bottom right) for AI assistance
                </p>
              </div>
            </div>
          )}
        </main>

        {/* Right rail */}
        <aside className="w-96 border-l border-[#00FF99]/20 bg-[#0B0B0B] overflow-y-auto">
          <RightRail
            metadata={null}
            onNavigate={handleNavigateToTagged}
            onMarkDone={onMarkDone}
            activePeer={activeDM ? (activeDM.user || activeDM) : null}
            filesBus={filesBus}
          />
        </aside>
        </div>
      </div>

      {/* Incoming-call card, call modal, recording-consent notice and busy
          banner now live in the app-wide CallProvider (context/CallContext.jsx),
          so they render on every authenticated page — not just here. */}
    </>
  );
};

// Offset the chat content beside the fixed sidebar. Values mirror
// src/components/chat/Sidebar.jsx exactly: 72px left rail on desktop,
// 62px bottom icon bar below the 720px breakpoint. Using a fixed box
// (not margins) keeps the full-height flex panes intact with no
// horizontal scrollbar. On mobile, bottom:62px reserves the bar's
// space so the message input is never covered by it.
const CHAT_SHELL_CSS = `
.kwchat-shell{
  position:fixed; top:0; right:0; bottom:0; left:72px;
  overflow:hidden;
}
@media (max-width:720px){
  .kwchat-shell{ left:0; bottom:62px; }
}
`;

export default ChatScreen;
